/**
 * Rasterize a live DOM node to a PNG.
 *
 * The card is already laid out by the browser at its intrinsic print size, so
 * the honest way to export it is to photograph that layout rather than redraw
 * it on a canvas: a second implementation would drift from the on-screen card
 * the moment either one changed.
 *
 * The technique is the standard one — wrap a clone of the node in an SVG
 * `<foreignObject>`, load that SVG as an image, and paint it into a canvas.
 * An SVG rendered this way is completely sealed off from the page: it cannot
 * reach the document's stylesheets, its fonts, or any URL on the network. So
 * everything the node depends on has to be carried inside the clone first,
 * which is what the three passes below do.
 */

/** Remote assets fetched once and reused across exports of both faces. */
const assetCache = new Map<string, string | undefined>();

/**
 * Fetch a remote asset as a data URL.
 *
 * Resolves to `undefined` rather than throwing when the host refuses CORS —
 * an avatar we aren't allowed to read is a missing picture, not a failed
 * export, and every caller has something reasonable to do about it.
 */
async function fetchDataUrl(url: string): Promise<string | undefined> {
  const cached = assetCache.get(url);
  if (cached !== undefined || assetCache.has(url)) return cached;

  let result: string | undefined;
  try {
    const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
    if (response.ok) {
      const blob = await response.blob();
      result = await new Promise<string | undefined>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : undefined);
        reader.onerror = () => resolve(undefined);
        reader.readAsDataURL(blob);
      });
    }
  } catch {
    result = undefined;
  }

  assetCache.set(url, result);
  return result;
}

/** Already-embedded sources need no work. */
function isInlineUrl(url: string): boolean {
  return url.startsWith('data:') || url.startsWith('blob:');
}

/**
 * Pass 1 — freeze the cascade.
 *
 * Inside the SVG there are no Tailwind classes and no stylesheet to match them
 * against, so every element carries its own fully-resolved declarations. The
 * two trees are walked in lockstep: `clone` is an exact structural copy of
 * `source`, so the nth element of one is the nth element of the other.
 */
function inlineComputedStyles(source: Element, clone: Element): void {
  const sourceNodes = [source, ...source.querySelectorAll('*')];
  const cloneNodes = [clone, ...clone.querySelectorAll('*')];

  for (let i = 0; i < sourceNodes.length; i++) {
    const target = cloneNodes[i];
    if (!(target instanceof HTMLElement) && !(target instanceof SVGElement)) continue;

    const computed = getComputedStyle(sourceNodes[i]);
    const declarations: string[] = [];
    for (const property of computed) {
      const value = computed.getPropertyValue(property);
      if (!value) continue;
      declarations.push(`${property}:${value}`);
    }
    target.setAttribute('style', declarations.join(';'));

    // Classes have done their job; leaving them in only bloats the payload.
    target.removeAttribute('class');
  }
}

/**
 * Pass 2 — embed the pictures.
 *
 * Avatars, badge artwork and favicons all live on other origins. Anything we
 * can't read is dropped: a hole where an image was is better than the broken
 * icon the renderer would otherwise leave behind.
 */
async function inlineImages(clone: Element): Promise<void> {
  const images = [...clone.querySelectorAll('img')];

  await Promise.all(
    images.map(async (image) => {
      const src = image.getAttribute('src');
      if (!src) {
        image.remove();
        return;
      }
      if (isInlineUrl(src)) return;

      const dataUrl = await fetchDataUrl(src);
      if (dataUrl) {
        image.setAttribute('src', dataUrl);
        // Lazy images may never be decoded inside a one-shot render.
        image.removeAttribute('loading');
      } else {
        image.remove();
      }
    }),
  );

  // Background and mask images ride in the style attribute rather than on an
  // element of their own, so they need the same treatment.
  const styled = [clone, ...clone.querySelectorAll('*')];
  await Promise.all(
    styled.map(async (element) => {
      if (!(element instanceof HTMLElement) && !(element instanceof SVGElement)) return;

      const style = element.getAttribute('style');
      if (!style || !/url\(["']?https?:/.test(style)) return;

      const urls = [...style.matchAll(/url\(["']?(https?:[^"')]+)["']?\)/g)].map((m) => m[1]);
      let rewritten = style;
      for (const url of urls) {
        const dataUrl = await fetchDataUrl(url);
        rewritten = rewritten.replaceAll(
          url,
          // A failed fetch leaves a reference the renderer can't follow, which
          // it simply skips — the element keeps its background color.
          dataUrl ?? 'about:blank',
        );
      }
      element.setAttribute('style', rewritten);
    }),
  );
}

/**
 * Pass 3 — carry the fonts.
 *
 * Theme fonts are registered on `document.fonts` at runtime, which the SVG
 * knows nothing about. Re-declaring them with the file inlined is the only way
 * the exported card matches the one on screen; when a font can't be fetched,
 * the same fallback stack the page uses takes over.
 */
async function fontFaceCss(urls: (string | undefined)[], families: (string | undefined)[]): Promise<string> {
  const rules = await Promise.all(
    urls.map(async (url, i) => {
      const family = families[i];
      if (!url || !family) return '';
      const dataUrl = await fetchDataUrl(url);
      if (!dataUrl) return '';
      return `@font-face{font-family:'${family}';src:url(${dataUrl});font-display:block;}`;
    }),
  );
  return rules.join('');
}

export interface RasterizeOptions {
  /** Intrinsic CSS size of the node. */
  width: number;
  height: number;
  /**
   * Output pixels per CSS pixel. The default puts a 3.5-inch card well past
   * 300 DPI, which is what a print shop asks for.
   */
  scale?: number;
  /**
   * Theme fonts to embed. Slots a theme leaves unset are passed through as
   * `undefined` and simply skipped, so callers can hand over their font list
   * as-is rather than filtering it first.
   */
  fonts?: ({ family?: string; url?: string } | undefined)[];
}

/**
 * Paint `node` into a PNG blob at its intrinsic size, scaled up for print.
 */
export async function rasterize(node: HTMLElement, options: RasterizeOptions): Promise<Blob> {
  const { width, height, scale = 3, fonts = [] } = options;

  const clone = node.cloneNode(true) as HTMLElement;
  inlineComputedStyles(node, clone);
  await inlineImages(clone);

  // The clone is positioned by the SVG, not by the page it came from.
  clone.style.position = 'static';
  clone.style.margin = '0';
  clone.style.width = `${width}px`;
  clone.style.height = `${height}px`;

  const css = await fontFaceCss(
    fonts.map((f) => f?.url),
    fonts.map((f) => f?.family),
  );

  const wrapper = document.createElement('div');
  wrapper.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
  if (css) {
    const style = document.createElement('style');
    style.textContent = css;
    wrapper.appendChild(style);
  }
  wrapper.appendChild(clone);

  const markup = new XMLSerializer().serializeToString(wrapper);
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" ` +
    `viewBox="0 0 ${width} ${height}">` +
    `<foreignObject x="0" y="0" width="${width}" height="${height}">${markup}</foreignObject>` +
    `</svg>`;

  // `encodeURIComponent` rather than base64: the markup is full of non-Latin1
  // characters (emoji shapes, names, bios) that `btoa` would reject outright.
  const source = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

  const image = new Image();
  image.decoding = 'sync';
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('The card could not be rendered to an image.'));
    image.src = source;
  });

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  const context = canvas.getContext('2d');
  if (!context) throw new Error('This browser has no 2D canvas to draw into.');
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('The image could not be encoded.'))),
      'image/png',
    );
  });
}

/** Hand a blob to the browser as a download. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoking immediately can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** A filename-safe stem, e.g. "Alice Q. Nostr" -> "alice-q-nostr". */
export function slugify(value: string): string {
  const slug = value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
  return slug || 'card';
}
